import { useEffect, useState } from 'react'
import { getDevice } from '@/lib/db'
import { countPending } from '@/lib/db'
import type { CachedAuftrag } from '@/lib/db'
import { flushOutbox } from '@/lib/sync'
import { EnrollPage } from '@/pages/EnrollPage'
import { KioskPage } from '@/pages/KioskPage'
import { MeinPage } from '@/pages/MeinPage'
import { AuftragAbschlussPage } from '@/pages/AuftragAbschlussPage'
import { PendingBanner } from '@/components/PendingBanner'

const SYNC_TAG = 'stempel-outbox-flush'
const POLL_INTERVAL_MS = 15_000

async function requestBackgroundSync() {
  if (!('serviceWorker' in navigator)) return
  const registration = await navigator.serviceWorker.ready
  const syncManager = (registration as ServiceWorkerRegistration & {
    sync?: { register: (tag: string) => Promise<void> }
  }).sync
  if (!syncManager) return
  try {
    await syncManager.register(SYNC_TAG)
  } catch {
    // Manche Browser lehnen Registrierung situativ ab -- Foreground-Poll faengt das auf.
  }
}

function KioskApp() {
  const [enrolled, setEnrolled] = useState<boolean | null>(null)
  const [pendingCount, setPendingCount] = useState(0)
  const [auftragInAbschluss, setAuftragInAbschluss] = useState<CachedAuftrag | null>(null)

  useEffect(() => {
    getDevice().then((device) => setEnrolled(!!device))
  }, [])

  useEffect(() => {
    if (!enrolled) return

    let cancelled = false

    async function refreshPendingCount() {
      const count = await countPending()
      if (!cancelled) setPendingCount(count)
    }

    async function trySync() {
      await flushOutbox()
      await refreshPendingCount()
    }

    refreshPendingCount()
    trySync()
    requestBackgroundSync()

    const interval = setInterval(trySync, POLL_INTERVAL_MS)
    window.addEventListener('online', trySync)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') trySync()
    })

    return () => {
      cancelled = true
      clearInterval(interval)
      window.removeEventListener('online', trySync)
    }
  }, [enrolled])

  if (enrolled === null) return null

  if (!enrolled) {
    return <EnrollPage onEnrolled={() => setEnrolled(true)} />
  }

  if (auftragInAbschluss) {
    return (
      <AuftragAbschlussPage
        auftrag={auftragInAbschluss}
        onDone={() => setAuftragInAbschluss(null)}
        onCancel={() => setAuftragInAbschluss(null)}
      />
    )
  }

  return (
    <>
      <KioskPage onAuftragAbschliessen={setAuftragInAbschluss} />
      <PendingBanner pendingCount={pendingCount} />
    </>
  )
}

// Einfaches path-basiertes Routing ohne React Router:
// /       → persoenliche Mitarbeiter-Ansicht (Keycloak-Auth, der Normalfall)
// /kiosk  → geteilter Kiosk (PIN-Modus, Device-Enrollment, Sonderfall)
export default function App() {
  if (window.location.pathname.startsWith('/kiosk')) {
    return <KioskApp />
  }
  return <MeinPage />
}
